import { useEffect } from "react";
import { NavLink } from "react-router";
import { useTranslation } from "react-i18next";
import { RoutesPaths } from "../Router/constant";

function About() {
  const { t } = useTranslation();
  const stats = t("pages.about.stats", { returnObjects: true });
  const pillars = t("pages.about.pillars.items", { returnObjects: true });
  const values = t("pages.about.values.items", { returnObjects: true });

  const pillarIcons = [
    "/images/capital-icons.svg",
    "/images/execution-icons.svg",
    "/images/governance-icons.svg",
  ];

  const valueIcons = [
    "/images/zap.png",
    "/images/integrity-icon.svg",
    "/images/local-icon.svg",
    "/images/thinking-icon.svg",
    "/images/disciplined-icon.svg",
  ];

  useEffect(() => {
    const elements = document.querySelectorAll(".reveal-on-scroll");

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const delay = entry.target.dataset.delay;
            if (delay) {
              entry.target.style.transitionDelay = `${delay}ms`;
            }

            entry.target.classList.add("show");
            obs.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="about-hero__overlay" />
        <div className="main-container about-hero__content">
          <div className="row justify-content-center">
            <div className="col-md-10 text-center">
              <h1
                className="m-0 fw-bold about-hero__title c-s-w reveal-on-scroll fade-up"
                data-delay="100"
              >
                {t("pages.about.hero.title")}
              </h1>
              <p
                className="m-0 f-s-20 l-h-30 c-s-w about-hero__text reveal-on-scroll fade-up"
                data-delay="150"
              >
                {t("pages.about.hero.text")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-stats">
        <div className="main-container">
          <div className="row g-0">
            {stats.map((item, index) => (
              <div
                className={`col-lg col-md-4 col-6${
                  stats.length % 2 !== 0 && index === stats.length - 1
                    ? " about-stats__col--center-mobile"
                    : ""
                }`}
                key={item.label}
              >
                <div className="about-stat">
                  <h2 className="m-0 f-w-700 c-s-w reveal-on-scroll fade-up" data-delay="200">
                    {item.value}
                  </h2>
                  <p className="m-0 f-s-16 l-h-28 c-s-w reveal-on-scroll fade-up" data-delay="250">
                    {item.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-story">
        <div className="main-container">
          <div className="row align-items-start g-5">
            <div className="col-md-6">
              <div className="about-story__media reveal-on-scroll fade-left" data-delay="200">
                <img src="/images/about1.png" className="img-fluid" alt="Printed financial newspapers" />
                <div className="about-story__badge">
                  <span>{t("pages.about.story.badgeLabel")}</span>
                  <strong>{t("pages.about.story.badgeTitle")}</strong>
                  <p className="m-0">{t("pages.about.story.badgeText")}</p>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="about-story__content">
                <h2
                  className="m-0 about-section__title f-s-48 l-h-60 reveal-on-scroll fade-right"
                  data-delay="150"
                >
                  {t("pages.about.story.title")}
                </h2>
                <span className="about-section__line" />
                <p className="m-0 f-s-18 l-h-30 c-p reveal-on-scroll fade-right" data-delay="200">
                  {t("pages.about.story.paragraphOne")}
                </p>
                <p className="m-0 f-s-18 l-h-30 c-p mt-3 reveal-on-scroll fade-right" data-delay="250">
                  {t("pages.about.story.paragraphTwo")}
                </p>
                <blockquote className="about-story__quote m-0 reveal-on-scroll fade-right" data-delay="300">
                  {t("pages.about.story.quote")}
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-section">
        <div className="main-container">
          <div className="about-section__heading text-center">
            <h2 className="m-0 about-section__title f-s-48 l-h-60 reveal-on-scroll fade-up" data-delay="150">
              {t("pages.about.pillars.title")}
            </h2>
            <p className="m-0 f-s-20 l-h-30 c-p reveal-on-scroll fade-up" data-delay="200">
              {t("pages.about.pillars.text")}
            </p>
          </div>

          <div className="row g-4">
            {pillars.map((pillar, index) => (
              <div className="col-md-4" key={pillar.id}>
                <article className="about-pillar reveal-on-scroll fade-up" data-delay="250">
                  <div className="about-pillar__top">
                    <div className="about-pillar__icon">
                      <img src={pillarIcons[index]} alt="" />
                    </div>
                    <span className="about-pillar__id">{pillar.id}</span>
                  </div>
                  <h3 className="f-s-24 l-h-32 fw-semibold m-0">{pillar.title}</h3>
                  <p className="m-0 f-s-16 l-h-30 c-p">{pillar.description}</p>
                  <div className="about-pillar__tags">
                    {pillar.tags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section section-who-we-are">
        <div className="main-container">
          <div className="row g-0 align-items-stretch justify-content-end">
            <div className="col-md-6 d-flex">
              <div className="who-content">
                <h2 className="m-0 f-w-600 f-s-48 l-h-60 c-s-w reveal-on-scroll fade-right" data-delay="150">
                  {t("pages.about.who.title")}
                </h2>
                <p className="m-0 f-s-20 l-h-30 c-s-w who-subtext reveal-on-scroll fade-right" data-delay="200">
                  {t("pages.about.who.subtitle")}
                </p>

                <div className="who-card reveal-on-scroll fade-right" data-delay="250">
                  <div className="card-top">
                    <div className="icon-box">
                      <img src="/images/icon-hand-money.png" alt="icon" />
                    </div>

                    <div className="arrow-box">
                      <img src="/images/arrow.png" alt="arrow" />
                      <img src="/images/arrow-dark.png" alt="arrow" />
                    </div>
                  </div>

                  <h6 className="m-0 f-w-600 f-s-24 l-h-32 c-s-w">
                    {t("pages.about.who.missionTitle")}
                  </h6>

                  <p className="m-0 f-s-16 l-h-24 c-s-w reveal-on-scroll fade-right" data-delay="300">
                    {t("pages.about.who.missionText")}
                  </p>
                </div>

                <div className="who-card mt-3 reveal-on-scroll fade-right" data-delay="350">
                  <div className="card-top">
                    <div className="icon-box">
                      <img src="/images/icon-hand-money.png" alt="icon" />
                    </div>

                    <div className="arrow-box">
                      <img src="/images/arrow.png" alt="arrow" />
                      <img src="/images/arrow-dark.png" alt="arrow" />
                    </div>
                  </div>

                  <h6 className="m-0 f-w-600 f-s-24 l-h-32 c-s-w">
                    {t("pages.about.who.visionTitle")}
                  </h6>

                  <p className="m-0 f-s-16 l-h-24 c-s-w reveal-on-scroll fade-right" data-delay="400">
                    {t("pages.about.who.visionText")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="about-section about-values">
        <div className="main-container">
          <div className="about-section__heading text-center">
            <h2 className="m-0 about-section__title f-s-48 l-h-60 reveal-on-scroll fade-left" data-delay="150">
              {t("pages.about.values.title")}
            </h2>
            <p className="m-0 f-s-20 l-h-30 c-p reveal-on-scroll fade-right" data-delay="200">
              {t("pages.about.values.text")}
            </p>
          </div>

          <div className="row g-4">
            {values.map((value, index) => (
              <div className={value.featured ? "col-md-8" : "col-md-4"} key={value.title}>
                <article
                  className={`about-value-card reveal-on-scroll fade-up${
                    value.featured ? " about-value-card" : ""
                  }`}
                >
                  <div className="about-value-card__icon">
                    <img src={valueIcons[index]} alt="" />
                  </div>
                  <h3 className="m-0 f-s-20 l-h-30">{value.title}</h3>
                  <p className="m-0 f-s-16 l-h-24">{value.description}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-document">
        <div className="main-container">
          <div className="about-document__heading text-center">
            <h2 className="m-0 about-section__title f-s-48 l-h-60 reveal-on-scroll fade-up" data-delay="150">
              {t("pages.about.document.title")}
            </h2>
            <p className="m-0 f-s-20 l-h-30 c-p reveal-on-scroll fade-up" data-delay="200">
              {t("pages.about.document.text")}
            </p>
          </div>

          <div className="about-document__viewer reveal-on-scroll fade-up" data-delay="250">
            <object
              data="/documents/provest-about-document.pdf#toolbar=0&navpanes=0&scrollbar=0&page=1&view=Fit"
              type="application/pdf"
              aria-label={t("pages.about.document.title")}
            >
              <div className="about-document__fallback">
                <p className="m-0 f-s-16 l-h-24 c-p">
                  {t("pages.about.document.fallback")}
                </p>
                <a
                  className="contact-btn d-flex align-items-center f-w-600 f-s-14 l-h-100 mx-auto"
                  href="/documents/provest-about-document.pdf"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="btn-text">{t("pages.about.document.open")}</span>
                  <span className="arrow-circle">
                    <img src="/images/right-angle-arrow.png" alt="icon" />
                  </span>
                </a>
              </div>
            </object>
          </div>
        </div>
      </section>

      <section className="section section-stratagic">
        <div className="main-container">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-9 text-center px-3">
              <h2 className="m-0 f-w-600 f-s-48 l-h-60 c-s about-cta__title reveal-on-scroll fade-up" data-delay="150">
                {t("common.siteCta.title")}
              </h2>
              <p className="m-0 f-s-20 l-h-30 c-s about-cta__text reveal-on-scroll fade-up" data-delay="200">
                {t("common.siteCta.text")}
              </p>

              <NavLink
                className="contact-btn d-flex align-items-center f-w-600 f-s-14 l-h-100 mx-auto"
                to={RoutesPaths.CONTACT}
              >
                <span className="btn-text">{t("common.cta.contact")}</span>
                <span className="arrow-circle">
                  <img src="/images/right-angle-arrow.png" alt="icon" />
                </span>
              </NavLink>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
